import { useCheckSealdSessions } from "apollo/hooks/mutations";
import { useGetFilesQuery, useGetPatientQuery } from "apollo/hooks/queries";
import { Result, composeProgress } from "apollo/utils";
import { ONTOLOGIES } from "core/model/utils/ontologies";
import { useGetOntology } from "core/model/utils/ontologies/hooks";
import { capitaliseFirst } from "core/model/utils/strings";
import { Auction, Patient, SealdSessionAccess } from "core/types";
import { format, fromUnixTime } from "date-fns";
import InfoChip from "ds_legacy/components/InfoChip";
import TextInputField from "ds_legacy/components/TextInputField";
import {
  ERROR_COLOR,
  SUCCESS_COLOR,
  TEXT_DISABLED_COLOR,
} from "ds_legacy/materials/colors";
import { HorizontalLayout, VerticalLayout } from "ds_legacy/materials/layouts";
import { padding } from "ds_legacy/materials/metrics";
import {
  Caption,
  FONT_SIZE_16,
  Subheading,
  Title,
} from "ds_legacy/materials/typography";
import { useAcpNavigationHandlers } from "dsl/hooks";
import { CheckIcon, XCircleIcon } from "lucide-react";
import { useEffect, useState } from "react";
import {
  FormRenderProps,
  convertModelDefinition,
  valueDef,
} from "react-forms-state";
import { useSearchParams } from "react-router-dom";
import { DetailsTable, DetailsTableRow, SearchButton } from "./shared";

const formatDate = (createdAt: number | null) =>
  createdAt ? format(fromUnixTime(createdAt), "dd/MM/yy - HH:mm:ss x") : "none";

export default function SessionPage({
  page,
}: {
  page: "file" | "patient" | "request";
}) {
  const [search, setSearch] = useSearchParams();
  const [sessionId, setSessionId] = useState<number | null>(null);

  const [patientQueryProgress, patient] = useGetPatientQuery({
    options: { skip: page !== "patient" || !sessionId },
    patientId: sessionId ?? -1,
  });
  const [
    checkSealdSessions,
    checkSealdSessionsQueryProgress,
    _,
    sealdSessionsPayload,
  ] = useCheckSealdSessions();

  const sealdSessions = sealdSessionsPayload
    ? sealdSessionsPayload.sealdSessions
    : undefined;

  const queryParamName =
    page === "patient"
      ? "patient_id"
      : page === "request"
      ? "auction_request_id"
      : "file_id";
  const sessionIdQueryparam = search.get(queryParamName);

  useEffect(() => {
    if (sessionIdQueryparam) {
      setSessionId(Number(sessionIdQueryparam));
    } else {
      setSessionId(null);
    }
  }, [sessionIdQueryparam]);

  useEffect(() => {
    if (sessionId) {
      checkSealdSessions({ [queryParamName]: sessionId });
    }
  }, [queryParamName, sessionId]);

  return (
    <>
      <HorizontalLayout aligned>
        <FormRenderProps
          formInputValue={{
            session_id: sessionId,
          }}
          modelDefinition={convertModelDefinition({
            ...valueDef("session_id"),
          })}
          onSubmit={({ session_id }: { session_id: string }) => {
            if (session_id) {
              setSessionId(Number(session_id));
              search.set(queryParamName, session_id);
              setSearch(search);
            }
          }}
        >
          {({ submit }) => (
            <>
              <TextInputField
                elementName="session_id"
                placeholder={`${capitaliseFirst(page)} ID`}
              />
              <SearchButton submit={submit} />
            </>
          )}
        </FormRenderProps>
      </HorizontalLayout>
      <Result
        data={{}}
        ErrorComponent={
          <Caption>
            Could not get {page} {sessionId}
          </Caption>
        }
        id="seald-session"
        queryProgress={composeProgress([
          patientQueryProgress,
          checkSealdSessionsQueryProgress,
        ])}
      >
        {() => (
          <>
            <Title>
              {capitaliseFirst(page)} {sessionId}
            </Title>
            <DetailsTable>
              <tbody>
                <DetailsTableRow>
                  <td
                    style={{
                      padding: padding(0),
                    }}
                  >
                    <Subheading>encryption activated for {page}</Subheading>
                  </td>
                  <td
                    style={{
                      padding: padding(0, 4),
                    }}
                  >
                    {sealdSessions?.seald_encryption_context_created_at ? (
                      <HorizontalLayout aligned>
                        <CheckIcon
                          size={FONT_SIZE_16}
                          style={{ color: SUCCESS_COLOR }}
                        />
                        <Caption>
                          {formatDate(
                            sealdSessions.seald_encryption_context_created_at,
                          )}
                        </Caption>
                      </HorizontalLayout>
                    ) : (
                      <XCircleIcon
                        size={FONT_SIZE_16}
                        style={{ color: ERROR_COLOR }}
                      />
                    )}
                  </td>
                </DetailsTableRow>
              </tbody>
            </DetailsTable>
            <Subheading>Sender accesses:</Subheading>
            {sealdSessions?.careseeker_accesses?.length ? (
              <AccessesDetailsTable
                accesses={sealdSessions.careseeker_accesses}
                type="sender"
              />
            ) : (
              <Caption>none</Caption>
            )}
            <Subheading>Receiver accesses:</Subheading>
            {sealdSessions?.careprovider_accesses?.length ? (
              <AccessesDetailsTable
                accesses={sealdSessions.careprovider_accesses}
                type="receiver"
              />
            ) : (
              <Caption>none</Caption>
            )}
            <Subheading>Account accesses:</Subheading>
            {sealdSessions?.account_accesses?.length ? (
              <AccessesDetailsTable
                accesses={sealdSessions.account_accesses}
                type="account"
              />
            ) : (
              <Caption>none</Caption>
            )}
            {page === "patient" && patient && (
              <FilesForPatient patient={patient} />
            )}
          </>
        )}
      </Result>
    </>
  );
}

function AccessesDetailsTable({
  accesses,
  type,
}: {
  accesses: Array<SealdSessionAccess>;
  type: "account" | "receiver" | "sender";
}) {
  const navigationHandlers = useAcpNavigationHandlers();
  return (
    <DetailsTable>
      <tbody>
        {accesses.map((access) => (
          <DetailsTableRow
            key={`${type}-${access.id}`}
            onClick={() => {
              if (type === "account") return;
              const page =
                type === "sender"
                  ? `sender?careseeker_id=${access.id}`
                  : `receiver?careprovider_id=${access.id}`;
              navigationHandlers.goToSealdDebug(page);
            }}
          >
            <td
              style={{
                padding: padding(0),
              }}
            >
              <Subheading>
                #{access.id} {access.name}
              </Subheading>
            </td>
            <td
              style={{
                padding: padding(0, 4),
              }}
            >
              {access.seald_db_access_created_at ? (
                <HorizontalLayout aligned>
                  <CheckIcon
                    size={FONT_SIZE_16}
                    style={{ color: SUCCESS_COLOR }}
                  />
                  <Caption>
                    {formatDate(access.seald_db_access_created_at)}
                  </Caption>
                </HorizontalLayout>
              ) : (
                <XCircleIcon
                  size={FONT_SIZE_16}
                  style={{ color: ERROR_COLOR }}
                />
              )}
            </td>
            <td
              style={{
                padding: padding(2, 4),
              }}
            >
              <VerticalLayout>
                <Caption color={TEXT_DISABLED_COLOR}>
                  seald_db_access:{" "}
                  {formatDate(access.seald_db_access_created_at)}
                </Caption>
                <Caption color={TEXT_DISABLED_COLOR}>
                  seald_access_created_at:{" "}
                  {formatDate(access.seald_access_created_at)}
                </Caption>
                <Caption color={TEXT_DISABLED_COLOR}>
                  pending_access: {formatDate(access.pending_access_created_at)}
                </Caption>
              </VerticalLayout>
            </td>
          </DetailsTableRow>
        ))}
      </tbody>
    </DetailsTable>
  );
}

function FilesForPatient({ patient }: { patient: Patient }) {
  return (
    <VerticalLayout>
      {patient.auctions?.map((auction) => (
        <FilesForAuction key={auction.id} auction={auction} />
      ))}
    </VerticalLayout>
  );
}

function FilesForAuction({ auction }: { auction: Auction }) {
  const navigationHandlers = useAcpNavigationHandlers();
  const getOntology = useGetOntology();
  const [filesQueryProgress, files] = useGetFilesQuery({
    params: `auction_id=${auction.id}`,
  });
  return (
    <>
      <Result
        data={files}
        ErrorComponent={
          <Caption>Could not get files for auction {auction.id}</Caption>
        }
        id="seald-session"
        queryProgress={filesQueryProgress}
      >
        {(files) => (
          <>
            <Title>Files for search {auction.id}</Title>
            {files.length ? (
              <DetailsTable>
                <tbody>
                  {files.map((file) => (
                    <DetailsTableRow
                      key={`file-${file.id}`}
                      onClick={() => {
                        navigationHandlers.goToSealdDebug(
                          `file?file_id=${file.id}`,
                        );
                      }}
                    >
                      <td
                        style={{
                          padding: padding(0),
                        }}
                      >
                        <Subheading>#{file.id}</Subheading>
                      </td>
                      <td
                        style={{
                          padding: padding(0, 4),
                        }}
                      >
                        <InfoChip
                          label={getOntology({
                            key: file.category,
                            type: ONTOLOGIES.FILE_CATEGORY,
                            useShortValues: true,
                          })}
                          tooltip={getOntology({
                            key: file.category,
                            type: ONTOLOGIES.FILE_CATEGORY,
                          })}
                        />
                      </td>
                      <td
                        style={{
                          padding: padding(0, 4),
                        }}
                      >
                        <Caption>{formatDate(file.created_at)}</Caption>
                      </td>
                    </DetailsTableRow>
                  ))}
                </tbody>
              </DetailsTable>
            ) : (
              <Caption>none</Caption>
            )}
          </>
        )}
      </Result>
    </>
  );
}
